/**
 * 🔍 Development Logger
 * 
 * 🔒 GDPR-SAFE: Logs OFF by default
 * Logs kan inneholde persondata (user_id, email, navn, subscription)
 * 
 * AKTIVERING (PROD):
 * 1. Åpne talentfunn.no
 * 2. I browser console: toggleDebug(true)
 * 3. Refresh siden (F5)
 * 4. Bruk appen → Logger lagres i localStorage
 * 5. Gå til Admin Portal → Dev Logger → Se alle logger
 * 
 * DEAKTIVERING:
 * - I browser console: toggleDebug(false)
 * - Se status: debugStatus()
 * 
 * BRUK:
 * - Import: import { devLog, devError } from '../utils/logger';
 * - Erstatt: console.log(...) → devLog(...)
 * - Kritiske errors (ingen persondata): prodError(...)
 */

/**
 * Check if debug logging is enabled
 */
const isDebugEnabled = (): boolean => {
  // ✅ GDPR-safe: Logs OFF by default
  // Må aktivt kjøre toggleDebug(true) i console for å aktivere
  const localEnabled = typeof window !== 'undefined' && 
                       localStorage.getItem('talentfunn_debug') === 'true';
  
  return localEnabled;
};

/**
 * Save log to localStorage for LogViewer
 */
const saveLog = (level: 'log' | 'error' | 'warn', message: string, data?: any) => {
  if (typeof window === 'undefined') return;
  
  try {
    const storedLogs = localStorage.getItem('talentfunn_dev_logs');
    const logs = storedLogs ? JSON.parse(storedLogs) : { logs: [] };
    
    logs.logs.push({
      timestamp: new Date().toISOString(),
      level,
      message,
      data
    });
    
    // Keep only last 1000 logs
    if (logs.logs.length > 1000) {
      logs.logs = logs.logs.slice(-1000);
    }
    
    localStorage.setItem('talentfunn_dev_logs', JSON.stringify(logs));
  } catch (e) {
    // Ignore localStorage errors
  }
};

/**
 * Development logger - erstatter console.log
 * Vises kun hvis debug mode er aktivert
 */
export const devLog = (...args: any[]) => {
  if (isDebugEnabled()) {
    console.log(...args);
    
    // Save to localStorage
    const message = args[0]?.toString() || 'Log';
    const data = args.length > 1 ? args.slice(1) : undefined;
    saveLog('log', message, data);
  }
};

/**
 * Development error logger - erstatter console.error
 * Vises kun hvis debug mode er aktivert
 */
export const devError = (...args: any[]) => {
  if (isDebugEnabled()) {
    console.error(...args);
    
    // Save to localStorage
    const message = args[0]?.toString() || 'Error';
    const data = args.length > 1 ? args.slice(1) : undefined;
    saveLog('error', message, data);
  }
};

/**
 * Development warning logger - erstatter console.warn
 * Vises kun hvis debug mode er aktivert
 */
export const devWarn = (...args: any[]) => {
  if (isDebugEnabled()) {
    console.warn(...args);
    
    // Save to localStorage
    const message = args[0]?.toString() || 'Warning';
    const data = args.length > 1 ? args.slice(1) : undefined;
    saveLog('warn', message, data);
  }
};

/**
 * Production-safe error logger
 * ALLTID vises (kritiske errors skal alltid logges)
 */
export const prodError = (...args: any[]) => {
  console.error(...args);
};

/**
 * Helper: Vis debug status i console
 */
export const showDebugStatus = () => {
  const localEnabled = typeof window !== 'undefined' && 
                       localStorage.getItem('talentfunn_debug') === 'true';
  
  console.log('🔍 Debug Status:');
  console.log('  localStorage (talentfunn_debug):', localEnabled);
  console.log('  Overall enabled:', localEnabled);
};

// Export for bruk i window console
if (typeof window !== 'undefined') {
  (window as any).toggleDebug = (enable: boolean) => {
    localStorage.setItem('talentfunn_debug', enable ? 'true' : 'false');
    console.log(`🔍 Debug mode ${enable ? 'AKTIVERT' : 'DEAKTIVERT'}`);
    console.log('💡 Refresh siden for å se endringer');
  };
  
  (window as any).debugStatus = showDebugStatus;
}
